import { FormEvent, useEffect, useRef, useState } from 'react';
import { api, ApiError } from './api';
import { useDisplayStore } from './store';

const CODE_LENGTH = 6;

export function PairingScreen() {
  const setPaired = useDisplayStore((s) => s.setPaired);
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  function update(v: string) {
    const clean = v
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, CODE_LENGTH);
    setCode(clean);
    setErr(null);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (code.length !== CODE_LENGTH) return;
    setBusy(true);
    setErr(null);
    try {
      const result = await api.pair(code);
      setPaired(result.displayToken, result.display);
    } catch (e) {
      setErr(humanize(e));
      setCode('');
      ref.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  const slots = Array.from({ length: CODE_LENGTH }, (_, i) => code[i] ?? '');

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-2xl text-center">
        <div className="text-3xl font-extrabold tracking-tight">
          Score<span className="text-brand">Lync</span>
        </div>
        <div className="mt-2 font-mono text-xs uppercase tracking-[0.3em] text-white/40">
          Display Endpoint
        </div>

        <h1 className="mt-12 text-4xl font-bold tracking-tight">
          Enter pairing code
        </h1>
        <p className="mt-3 text-white/60">
          Get a 6-character code from the operator console at{' '}
          <span className="font-mono">app.scorelync.com</span> → Venues →
          Add display.
        </p>

        <form onSubmit={submit} className="mt-12">
          <div className="relative">
            <input
              ref={ref}
              value={code}
              onChange={(e) => update(e.target.value)}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              inputMode="text"
              maxLength={CODE_LENGTH}
              className="absolute inset-0 h-full w-full cursor-pointer text-transparent caret-transparent opacity-0"
            />
            <div className="pointer-events-none flex justify-center gap-3">
              {slots.map((c, i) => (
                <div
                  key={i}
                  className={`flex h-24 w-20 items-center justify-center rounded-2xl border-2 font-mono text-5xl font-bold transition ${
                    i === code.length
                      ? 'border-brand bg-brand/10'
                      : c
                      ? 'border-brand/50 bg-white/5'
                      : 'border-white/15 bg-white/5'
                  }`}
                >
                  {c}
                </div>
              ))}
            </div>
          </div>

          {err && (
            <div className="mt-8 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={code.length !== CODE_LENGTH || busy}
            className="mt-8 inline-flex items-center justify-center rounded-xl bg-brand px-8 py-4 text-lg font-semibold shadow-lg shadow-brand/30 transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy ? 'Pairing…' : 'Pair display →'}
          </button>
        </form>
      </div>
    </div>
  );
}

function humanize(e: unknown): string {
  if (!(e instanceof ApiError)) return 'Network error — check connection.';
  switch (e.code) {
    case 'pairing_code_invalid':
      return 'That code isn\'t recognized. Check it and try again.';
    case 'pairing_code_expired':
      return 'Pairing code expired. Generate a new one from the operator console.';
    default:
      return e.message;
  }
}
